// Inputs and validation for the signed-out landing page's business case builder.
//
// Free-entry fields are the visitor's own figures. Outcome assumptions are not: they are chosen
// from research-informed scenarios that the server defines and validates, because the resulting
// number goes out under FutureLab's name. Prices and evidence copy arrive as props from
// app/models/business_case/pricing.rb and app/models/business_case/evidence.rb — never restated
// here. The case itself is calculated once, server-side, in app/interactors/business_case/calculate.rb.

import { isWorkEmail } from "../contact";

export type Objective = "retention" | "productivity" | "leadership" | "onboarding";

export type NumberFieldName =
  | "participants"
  | "salary"
  | "turnoverRate"
  | "turnoverCostPct"
  | "openRoles"
  | "externalHirePremiumPct"
  | "newHires"
  | "hourlyRate";

export type ChoiceFieldName =
  | "reductionPct"
  | "productivityLiftPct"
  | "promotionIncreasePct"
  | "rampDaysSaved";

export type FieldName = NumberFieldName | ChoiceFieldName;

export type Programme = { objective: Objective } & Record<FieldName, number>;

export type Contact = {
  name: string;
  email: string;
  organization: string;
  companySize: string;
  role: string;
  consent: boolean;
};

export type EvidenceSource = { label: string; url: string };

export type ObjectiveEvidence = {
  field: ChoiceFieldName;
  hint: string;
  options: { value: number; label: string; display: string }[];
  summary: string;
  caveat: string;
  sources: EvidenceSource[];
};

export type Evidence = Record<Objective, ObjectiveEvidence>;

export type AddOnDuration = { months: number; price: number; label: string };

export type AddOn = {
  key: string;
  name: string;
  unit: "flat" | "per_mentor_group" | "duration";
  price: number | null;
  priceLabel: string;
  description: string;
  // Present only on `duration` add-ons, where the buyer picks the support term.
  durations?: AddOnDuration[];
  defaultMonths?: number;
};

export type SupportLevel = {
  key: string;
  name: string;
  tagline: string;
  description: string;
  addOns: string[];
};

export type AnnualTier = {
  key: string;
  quoteName: string;
  planName: string;
  maxParticipants: number | null;
  annualPrice: number | null;
};

export type Pricing = {
  currency: string;
  setupFee: number;
  annualMonths: number;
  mentorsPerTrainingGroup: number;
  pilot: {
    key: string;
    name: string;
    price: number;
    months: number;
    maxParticipants: number;
    setupFee: number;
    summary: string;
  };
  annualTiers: AnnualTier[];
  addOns: AddOn[];
  supportLevels: SupportLevel[];
  planIncludes: string[];
  platformIncludes: string[];
  promise: { hours: number; minimumParticipants: number; refund: number };
};

// Mirrors BusinessCaseLead::ROLES; keep both in step. Company sizes, the legal links and the
// work-address rule are shared with the enquiry form and live in ../contact.
export const ROLES = ["HR/People", "L&D", "People Ops", "Other"] as const;

export { COMPANY_SIZES, PRIVACY_URL, TERMS_URL } from "../contact";

export const CONFIG = {
  version: "research-informed-business-case-usd-v5",
  locale: "en-US",
  defaults: {
    objective: "retention" as Objective,
    participants: 120,
    salary: 15_000,
    turnoverRate: 20,
    turnoverCostPct: 75,
    reductionPct: 5,
    productivityLiftPct: 1,
    openRoles: 10,
    promotionIncreasePct: 10,
    externalHirePremiumPct: 50,
    newHires: 40,
    rampDaysSaved: 10,
    hourlyRate: 12.5,
  },
} as const;

export const OBJECTIVES: Record<
  Objective,
  { label: string; fields: readonly FieldName[] }
> = {
  retention: {
    label: "Improve retention",
    fields: ["turnoverRate", "turnoverCostPct", "reductionPct"],
  },
  productivity: {
    label: "Boost productivity",
    fields: ["productivityLiftPct"],
  },
  leadership: {
    label: "Build leadership pipeline",
    fields: ["openRoles", "promotionIncreasePct", "externalHirePremiumPct"],
  },
  onboarding: {
    label: "Speed up onboarding",
    fields: ["newHires", "rampDaysSaved"],
  },
};

export const CHOICE_FIELDS: Record<ChoiceFieldName, { label: string }> = {
  reductionPct: { label: "Expected turnover reduction from mentoring" },
  productivityLiftPct: { label: "Expected productivity increase from mentoring" },
  promotionIncreasePct: { label: "Increase in internal promotion rate from mentoring" },
  rampDaysSaved: { label: "Days saved to full productivity" },
};

export const isChoiceField = (name: FieldName): name is ChoiceFieldName => name in CHOICE_FIELDS;

export const FIELDS: Record<
  NumberFieldName,
  { label: string; hint: string; min: number; max: number; step: number }
> = {
  participants: {
    label: "Number of participants",
    hint: "Total mentors + mentees. Count each person once.",
    min: 1,
    max: 1_000_000,
    step: 1,
  },
  salary: {
    label: "Average employee salary (USD/yr)",
    hint: "Annual salary per participant, in US dollars.",
    min: 1,
    max: 10_000_000,
    step: 0.01,
  },
  turnoverRate: {
    label: "Current annual turnover rate (%)",
    hint: "Share of participants expected to leave each year.",
    min: 0,
    max: 100,
    step: 0.1,
  },
  turnoverCostPct: {
    label: "Cost of replacing an employee (% of salary)",
    hint: "Recruiting, onboarding and other replacement costs.",
    min: 0,
    max: 500,
    step: 0.1,
  },
  openRoles: {
    label: "Leadership / senior roles to fill this year",
    hint: "Roles that could be filled through internal promotion.",
    min: 0,
    max: 100_000,
    step: 1,
  },
  externalHirePremiumPct: {
    label: "Extra cost of hiring externally (% of salary)",
    hint: "Additional external hiring cost compared with promoting internally.",
    min: 0,
    max: 500,
    step: 0.1,
  },
  newHires: {
    label: "New hires onboarded through mentoring per year",
    hint: "Annual new-hire volume; this can include multiple cohorts.",
    min: 0,
    max: 1_000_000,
    step: 1,
  },
  hourlyRate: {
    label: "Loaded hourly rate of programme admin (USD/hr)",
    hint: "Hourly pay plus employer costs, in US dollars.",
    min: 0,
    max: 100_000,
    step: 0.01,
  },
};

export const defaultProgramme: Programme = { ...CONFIG.defaults };


export function validateProgramme(programme: Programme): string | null {
  const invalid = (Object.keys(FIELDS) as NumberFieldName[]).find((name) => {
    const value = programme[name];

    return !Number.isFinite(value) || value < FIELDS[name].min || value > FIELDS[name].max;
  });

  if (!invalid) return null;

  return `Check ${FIELDS[invalid].label.toLowerCase()} — it needs to be between ${FIELDS[invalid].min} and ${FIELDS[invalid].max}.`;
}

export function validateContact(contact: Contact): string | null {
  if (!contact.name.trim()) return "Add your name so we know who the business case is for.";
  if (!contact.organization.trim()) return "Add your company name.";
  if (!contact.email.trim()) return "Add your work email.";
  if (!isWorkEmail(contact.email)) {
    return "Use your work email — we can't send a business case to a personal inbox.";
  }
  if (!contact.companySize) return "Choose your company size.";
  if (!contact.role) return "Choose the role that best describes you.";
  if (!contact.consent) return "Tick the consent box so we can send your business case.";

  return null;
}

const MONEY_FORMAT = new Intl.NumberFormat(CONFIG.locale, { maximumFractionDigits: 0 });
const CENTS_FORMAT = new Intl.NumberFormat(CONFIG.locale, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const DECIMAL_FORMAT = new Intl.NumberFormat(CONFIG.locale, { maximumFractionDigits: 1 });

// Matches BusinessCase::Calculate#money so the page, the email and the PDF read identically,
// cents included only when the figure has them.
export const money = (value: number) => {
  const amount = Math.round(value * 100) / 100;

  return Number.isInteger(amount)
    ? `$${MONEY_FORMAT.format(amount)}`
    : `$${CENTS_FORMAT.format(amount)}`;
};

export const decimal = (value: number) => DECIMAL_FORMAT.format(value);
